import "../styles/globals.css";
import "typeface-rubik";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-toastify/dist/ReactToastify.css";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { ToastContainer } from "react-toastify";
import { ColorSchemeScript, MantineProvider } from "@mantine/core";
import { theme } from "../theme";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { Rubik } from "next/font/google";
import { PageContext } from "@hooks/utils/context";
import localFont from "next/font/local";
import Script from "next/script";
import { CounterStoreProvider } from "providers/counter-store-provider";
import ErrorBoundary from "@components/ErrorBoundary";

const avocados = localFont({
  src: "../public/ethinos-home-page/fonts/Avocados.ttf",
  variable: "--font-avocados",
  display: "swap",
  preload: true,
  fallback: ["Arial", "sans-serif"],
});

const rubik = Rubik({ subsets: ["latin"] });

export default function App({ Component, pageProps }) {
  const router = useRouter();
  const currentRoute = router.asPath;

  // Enhanced check for PSBU route using regex:
  // This returns true if the route exactly equals "/psbu" or starts with "/psbu/"
  const isPsbuRoute = currentRoute ? /^\/psbu(\/|$)/.test(currentRoute) : false;

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);

  const backgroundImageRouteList = [
    "/Empower",
    "/ReAssure2",
    "/appforms/senior-first",
    "/appforms/reassurev2",
    "/appforms/aspire",
    "/appforms/health-recharge",
    "/appforms/health-premia",
  ];
  // console.log(
  //   backgroundImageRouteList.some((element) => currentRoute.startsWith(element))
  // );

  useEffect(() => {
    const loadImage = async () => {
      const res =
        currentRoute !== "/travelLogin"
          ? await fetch("/aem-to-react/stethPhoto.png")
          : await fetch("travelBackground.jpg");
      const imageBlob = await res.blob();
      setBackgroundImage(URL.createObjectURL(imageBlob));
      setIsImageLoaded(true);
    };
    backgroundImageRouteList.some((element) =>
      currentRoute.startsWith(element)
    ) && loadImage();
  }, [currentRoute]);

  const mainClass = backgroundImageRouteList.some((element) =>
    currentRoute.startsWith(element)
  )
    ? `${rubik.className} ${
        isImageLoaded
          ? `ease-linear bg-cover bg-no-repeat`
          : `bg-gradient-to-br from-[#da6218] to-[#DD7230]`
      } w-screen h-screen overflow-auto`
    : currentRoute.startsWith("/Travel") || currentRoute.startsWith("/ohi")
    ? `${rubik.className} bg-white w-full h-full overflow-auto`
    : currentRoute == "/" ||
      currentRoute == "/v1" ||
      currentRoute == "/health-insurance-plan/get-quote" ||
      currentRoute == "/health-insurance-plan/aspire" ||
      currentRoute == "/health-insurance-plans.html" ||
      currentRoute == "/family-health-insurance-plans.html" ||
      currentRoute == "/individual-health-insurance-plans.html" ||
      currentRoute == "/travel-insurance.html" ||
      currentRoute == "/mediclaim.html" ||
      currentRoute == "/mediclaim"
    ? `${rubik.className} bg-[#F3F7F8] ${avocados.variable} w-full h-full overflow-y-auto overflow-x-hidden`
    : currentRoute.startsWith("/disease-a-z")
    ? `${rubik.className} bg-[#F3F7F8] `
    : currentRoute.startsWith("/appform") ||
      currentRoute.startsWith("/telecaller-dashboard-magnus")
    ? `${rubik.className} bg-[#F3F7F8] w-full h-screen overflow-y-auto overflow-x-hidden`
    : currentRoute.startsWith("/corporate-group-health-insurance")
    ? `${rubik.className} ${avocados.variable}`
    : currentRoute.startsWith("/health-insurance-plan/summary/")
    ? `${rubik.className} bg-[#F3F7F8] w-full overflow-y-auto `
    : `${rubik.className} bg-[#F3F7F8] w-full`;

  const style = backgroundImageRouteList.some((element) =>
    currentRoute.startsWith(element)
  )
    ? { backgroundImage: `url(${backgroundImage})` }
    : { backgroundImage: `` };

  const [swipeDirection, setSwipeDirection] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      if (!currentRoute.includes("shallow")) {
        containerRef.current.scrollTop = 0;
      }
    }
  }, [currentRoute]);

  useEffect(() => {
    const start = (url) => {
      setPageLoading(true);
    };
    router.events.on("routeChangeStart", start);

    const end = (url) => {
      setPageLoading(false);
    };
    router.events.on("routeChangeComplete", end);

    return () => {
      router.events.off("routeChangeStart", start);
      router.events.off("routeChangeComplete", end);
    };
  }, [router.events]);

  useEffect(() => {
    const language = localStorage.getItem("vitra_currentLanguage") || "english";
    document.body.className = `lang_${language}`;
    if(currentRoute.startsWith("/family-health-insurance-plans")) {
      document.body.className = `lang_english`
    }
  }, []);

  return (
    <main ref={containerRef} className={mainClass} style={style}>
      <CounterStoreProvider>
      <PageContext.Provider value={{ pageLoading }}>
        <ColorSchemeScript defaultColorScheme="light" />
        <MantineProvider theme={theme}>
          <ErrorBoundary>
          <ToastContainer />
          <Component {...pageProps} />
          <Script
            id="bug-resolver"
            strategy="beforeInteractive"
            src="https://code.jquery.com/jquery-3.6.0.min.js"
          />
            {isPsbuRoute ? (
             <Script id="googletagmanager" strategy="lazyOnload">
             {`
               (function(w, d, s, l, i) {
                 w[l] = w[l] || [];
                 w[l].push({
                   'gtm.start': new Date().getTime(),
                   event: 'gtm.js'
                 });
                 var f = d.getElementsByTagName(s)[0],
                     j = d.createElement(s),
                     dl = l !== 'dataLayer' ? '&l=' + l : '';
                 j.async = true;
                 j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
                 f.parentNode.insertBefore(j, f);
               })(window, document, 'script', 'dataLayer', 'GTM-5MP2MZX9');
             `}
           </Script>
           
            ) : (
              <Script id="googletagmanager" strategy="lazyOnload">
                {`(function (w, d, s, l, i) {
                w[l] = w[l] || [];
              w[l].push({
                'gtm.start': new Date().getTime(),
              event: 'gtm.js'
            });
              var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
              j.async = true;
              j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
              f.parentNode.insertBefore(j, f);
                })(window, document, 'Script', 'dataLayer', 'GTM-ML22GCT');`}
              </Script>
            )}
          <Script id="lemnisk-tag" type="text/javascript">
            {`window.lmSMTObj = window.lmSMTObj || [];
var methods = [
    "init",
    "page",
    "track",
    "identify"
]
for (var i=0; i<methods.length; i++) {
    lmSMTObj[methods[i]] = function(methodName){
        if(methodName !== "init") {
          return function() {
              lmSMTObj.push([methodName].concat(Array.prototype.slice.call(arguments)));
              var fn = arguments[arguments.length-1];
              setTimeout(function() {
                if(typeof fn === "function" && !lmSMTObj.smartTagversion) {
                  fn();
                  lmSMTObj.length > 0 ? lmSMTObj[lmSMTObj.length-1].push("callbackEnded"): null;
                }
              }, 2000);

            }
        }
        return function(){
            lmSMTObj.push([methodName].concat(Array.prototype.slice.call(arguments)));
        };
    }(methods[i]);

}
lmSMTObj.init("dpxml3k6cmk5bll6p0wn");

var a = document.createElement("script");
a.type = "text/javascript";
a.async = !0;
a.src = "//cdn25.lemnisk.co/ssp/st/6323.js";
var b = document.getElementsByTagName("script")[0];
b.parentNode.insertBefore(a, b);`}
          </Script>
          </ErrorBoundary>
        </MantineProvider>
      </PageContext.Provider>
      </CounterStoreProvider>
    </main>
  );
}
